<template>
  <div class="naver-autocomplete-wrapper">
    <v-combobox
      ref="combobox"
      v-model="selectedItem"
      class="font-13"
      :items="naverSearchResult"
      :search-input.sync="searchText"
      item-text="title"
      item-value="title"
      placeholder="업체명을 입력해주세요."
      no-data-text="검색 결과가 없습니다."
      :auto-select-first="false"
      autocomplete="off"
      solo
      flat
      single-line
      hide-details
      height="48"
      :menu-props="{ contentClass: 'naver-autocomplete-menu', maxWidth: '100%', minWidth: '100%', nudgeBottom: 8 }"
      attach>
      <!-- 돋보기 아이콘 -->
      <template v-slot:append>
        <v-icon color="#757575">mdi-magnify</v-icon>
      </template>

      <template v-slot:item="{ item }">
        <v-list-item-content v-if="item.id === DIRECT_INPUT_MARKER">
          <v-list-item-title>직접 입력: {{ item.originalText }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-content v-else>
          <v-list-item-title v-html="item.title"></v-list-item-title>
          <v-list-item-subtitle>{{ item.roadAddress }}</v-list-item-subtitle>
        </v-list-item-content>
      </template>
      <template v-slot:no-data>
        <div v-if="isFetching">
          <v-skeleton-loader max-height="52" class="pa-4" type="paragraph"></v-skeleton-loader>
          <v-skeleton-loader max-height="52" class="pa-4" type="paragraph"></v-skeleton-loader>
          <v-skeleton-loader max-height="52" class="pa-4" type="paragraph"></v-skeleton-loader>
        </div>
        <v-list-item v-else-if="searchText && searchText.length > 0" @click="handleDirectInput">
          <v-list-item-content>
            <v-list-item-title>직접 입력: {{ searchText }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-combobox>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NaverAutoComplete',
  data() {
    return {
      naverSearchResult: this.companyName ? [{ title: this.companyName, mapx: this.x, mapy: this.y }] : [],
      isFetching: false,
      searchText: '',
      DIRECT_INPUT_MARKER: '__DIRECT_INPUT__',
    };
  },
  props: {
    companyName: {
      type: String,
      required: true,
    },
    x: {
      type: String,
    },
    y: {
      type: String,
    },
    roadAddress: {
      type: String,
    },
  },
  computed: {
    selectedItem: {
      get() {
        return this.companyName;
      },
      set(value) {
        // 문자열인 경우 (직접 입력)
        if (typeof value === 'string') {
          this.$emit('update:all', {
            companyName: value,
            x: '',
            y: '',
            roadAddress: '',
          });
          return;
        }

        if (value && typeof value === 'object') {
          // 직접 입력 옵션 선택
          if (value.id === this.DIRECT_INPUT_MARKER) {
            this.$emit('update:all', {
              companyName: this.searchText,
              x: '',
              y: '',
              roadAddress: '',
            });
            this.naverSearchResult = [];
            this.$nextTick(() => {
              this.$refs.combobox && this.$refs.combobox.blur();
            });
            return;
          }

          // 일반 검색 결과 선택
          const addressToUse = value.roadAddress || value.address || '';
          this.$emit('update:all', {
            companyName: value.title,
            x: value.mapx,
            y: value.mapy,
            roadAddress: addressToUse,
          });
        }
      },
    },
  },

  methods: {
    handleDirectInput() {
      if (this.searchText && this.searchText.trim()) {
        this.selectedItem = this.searchText;
        this.naverSearchResult = [];
        this.$nextTick(() => {
          this.$refs.combobox && this.$refs.combobox.blur();
        });
      }
    },
  },

  watch: {
    searchText(value) {
      clearTimeout(this.searchDebounceTimer);

      if (!value || value.trim() === '') {
        this.naverSearchResult = [];
        return;
      }

      this.searchDebounceTimer = setTimeout(async () => {
        if (this.companyName === value) {
          return;
        }

        this.naverSearchResult = [];

        try {
          this.isFetching = true;
          const result = await axios
            .get(`/utils/search/local?q=${value}&display=5`, { loading: false })
            .then(res => {
              return res.data;
            })
            .catch(() => {
              // Error handling
            });

          const formattedResult = result.data.items.map(item => ({
            ...item,
            title: item.title.replace(/<b>/g, '').replace(/<\/b>/g, '').replace(/&amp;/g, '&'),
          }));

          // 검색 결과에 "직접 입력" 옵션 추가
          const directInputOption = {
            id: this.DIRECT_INPUT_MARKER,
            title: value, // 실제 입력될 값
            originalText: value, // 표시용 텍스트
            roadAddress: '',
            mapx: '',
            mapy: '',
          };

          this.naverSearchResult = [...formattedResult, directInputOption];
        } catch {
          // Error handling
        } finally {
          this.isFetching = false;
        }
      }, 500);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../assets/scss/typo.scss';

.naver-autocomplete-wrapper {
  width: 100%;
}

::v-deep .v-autocomplete {
  .v-input__control {
    min-height: auto !important;
  }

  .v-input__slot {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 16px !important;
    gap: 8px;
    width: 100%;
    height: 48px !important;
    min-height: 48px !important;
    background: #ffffff !important;
    border: 1px solid #dbdbdb !important;
    border-radius: 8px !important;
    margin-bottom: 0 !important;
    box-shadow: none !important;
  }

  /* focus 상태 */
  &.v-input--is-focused {
    .v-input__slot {
      border-color: #3a3a3a !important;
    }
  }

  input {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 14px !important;
    line-height: 18px;
    color: #3a3a3a !important;
    padding: 0 !important;
  }

  input::placeholder {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 14px !important;
    color: #dbdbdb !important;
  }

  .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center !important;

    .v-icon {
      color: #757575 !important;
    }
  }
}

/* 드롭다운 메뉴 스타일 */
::v-deep .naver-autocomplete-menu {
  margin-top: 0 !important;

  .v-list {
    background: #ffffff;
    border: 1px solid #f5f5f5;
    border-radius: 8px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  }

  .v-list-item__content {
    padding: 0 !important;
  }

  .v-list-item {
    min-height: 30px;
    padding: 12px 16px;
    font-family: 'Spoqa Han Sans Neo';
    font-size: 14px !important;
    line-height: 18px !important;
    font-weight: 400 !important;
    color: #202020 !important;

    &:hover {
      background: #f5f5f5 !important;
    }

    &.v-list-item--active {
      background: #f5f5f5 !important;
      color: #202020 !important;

      .v-icon {
        color: #3a3a3a !important;
      }
    }
  }
}
</style>
